import React, {Fragment, useEffect, useState} from "react";
import Drawer from "@material-ui/core/Drawer";
import clsx from "clsx";
import List from "@material-ui/core/List";
import {CircularProgress, makeStyles} from "@material-ui/core";
import {useOrganization} from "../organizations/organization-context";
import {
    getOrganizationUnit,
    getOrganizationUnitCount,
    getTopOrganizationUnits,
    searchOrganizationUnitChildren,
} from "../../api/organization-unit-functions";
import {GreyedOutProgressMask} from "../ui/GreyedOutProgressMask";
import {OrganizationUnitListItem} from "./OrganizationUnitsBrowserListItem";
import {run} from "../../utils/function-util";
import {useApiNotification} from "../ui/ApiNotificationProvider";
import {LazyLoadedList} from "../ui/LazyLoadedList";
import ListItem from "@material-ui/core/ListItem";

const useStyles = makeStyles(theme => ({
    topSpacer: {
        ...theme.mixins.toolbar,
    },
    drawerPaper: {
        position: 'static',
        width: '100%',
        border: 'none'
    },
    drawer: {
        height: '100%'
    },
    listPadding: {
        padding: '0'
    },
    drawerList: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: "hidden"
    },
    childrenList: {
        height: '100%',
        overflowY: 'hidden'
    },
    orgUnitPlaceholder: {
        height: '48px',
        justifyContent: 'center'
    },
    orgUnitChildrenContainer: {
        flex: '1 1 auto',
        'overflow-x': 'hidden'
    }
}));
export const OrganizationUnitsBrowser = (props: any) => {
    const classes = useStyles();
    const [currentOrganization] = useOrganization();
    const [browserIsLoading, setBrowserIsLoading] = useState(false);
    const [organizationUnits, setOrganizationUnits] = useState<any[]>([]);
    const [organizationUnitChildren, setOrganizationUnitChildren] = useState<any[]>([]);
    const [organizationUnitChildrenCount, setOrganizationUnitChildrenCount] = useState(0);
    const currentOrganizationUnitId = props.organizationUnitId;
    const raiseOrganizationUnitIdChanged = props.onOrganizationUnitIdChanged;
    const organizationUnitContextMenuOptions = props.organizationUnitContextMenuOptions;
    const raiseOrganizationUnitContextMenuOptionClicked = props.onOrganizationUnitContextMenuOptionClicked;
    const organizationUnitSearch = props.organizationUnitSearch;
    const filter = props.filter;
    const {handleError} = useApiNotification();

    useEffect(run(async () => currentOrganization ? await loadOrganizationUnits(currentOrganization.id, currentOrganizationUnitId) : Promise.resolve([]), setOrganizationUnits, handleError, () => setBrowserIsLoading(false)), [currentOrganization, currentOrganizationUnitId]);
    const loadOrganizationUnits = async (organizationId: any, organizationUnitId: any) => {
        setBrowserIsLoading(true);
        const topOrganizationUnits = await getTopOrganizationUnits(organizationId);
        if (organizationUnitId && topOrganizationUnits.some((top: any) => top.id === organizationUnitId)) {
            return getDisplayedTopOrganizationUnits(organizationId, organizationUnitId, topOrganizationUnits);
        } else {
            return getDisplayedOrganizationUnits(organizationId, currentOrganizationUnitId, topOrganizationUnits);
        }
    };
    useEffect(run(() => {
        setOrganizationUnitChildrenCount(0);
        return currentOrganization && organizationUnits && organizationUnits.length ? getOrganizationUnitCount(currentOrganization.id, organizationUnits.find(o => o.isSelected)?.id, filter) : Promise.resolve(0);
    }, (count: any) => {
        setOrganizationUnitChildren(new Array(count).fill(null));
        setOrganizationUnitChildrenCount(count);
    }, handleError), [organizationUnits, filter]);
    const isItemLoaded = (index: any) => {
        return !!organizationUnitChildren[index];
    };
    const childRow = ({index, style}: any) => {
        if (!isItemLoaded(index)) {
            return (<ListItem key={`loading-ou-${index}`} style={style} className={classes.orgUnitPlaceholder}>
                <CircularProgress size={25}/>
            </ListItem>);
        }
        return (<div style={style}>
            <OrganizationUnitListItem organizationId={currentOrganization?.id}
                                      organizationUnit={organizationUnitChildren[index]} isDisplayedAsChild={true}
                                      onOrganizationUnitIdChanged={raiseOrganizationUnitIdChanged}
                                      onOrganizationUnitContextMenuOptionClicked={raiseOrganizationUnitContextMenuOptionClicked}
                                      organizationUnitContextMenuOptions={organizationUnitContextMenuOptions}/>
        </div>);
    };
    const loadChildren = (startIndex: any, stopIndex: any) => {
        const from = startIndex;
        const size = stopIndex - startIndex + 1;
        return searchOrganizationUnitChildren(currentOrganization!.id, organizationUnits.find(o => o.isSelected)?.id, from, size, filter)
            .then(resp => {
                setOrganizationUnitChildren(prev => {
                    return [...prev.slice(0, startIndex), ...resp, ...prev.slice(stopIndex + 1)];
                });
            })
            .catch(handleError);
    };
    const displayedChildren = (<Fragment>
        {!!organizationUnitChildrenCount && organizationUnitChildrenCount > 0 &&
        <div className={classes.orgUnitChildrenContainer}>
            <LazyLoadedList className={classes.childrenList} loadMoreItems={loadChildren} item={childRow}
                            itemCount={organizationUnitChildrenCount} isItemLoaded={isItemLoaded} itemSize={50}/>
        </div>}</Fragment>);
    const containerStyle = {
        'width': '100%'
    };
    return (<GreyedOutProgressMask isActive={browserIsLoading} displaySpinner={true} containerStyle={containerStyle}>
        <Drawer key='organization-unit-browser' variant='permanent' classes={{paper: clsx(classes.drawerPaper)}}
                className={classes.drawer}>
            <List classes={{padding: clsx(classes.listPadding)}} className={classes.drawerList}>
                {organizationUnits && organizationUnits.map(organizationUnit => <OrganizationUnitListItem
                    onOrganizationUnitIdChanged={raiseOrganizationUnitIdChanged}
                    onOrganizationUnitContextMenuOptionClicked={raiseOrganizationUnitContextMenuOptionClicked}
                    organizationUnitContextMenuOptions={organizationUnitContextMenuOptions}
                    key={`organization-unit-list-item-${organizationUnit.id}`}
                    organizationId={currentOrganization?.id} organizationUnit={organizationUnit}
                    children={displayedChildren} isDisplayedAsChild={false}
                    organizationUnitSearch={organizationUnitSearch}/>)}

            </List>
        </Drawer>
    </GreyedOutProgressMask>);
};
const getDisplayedTopOrganizationUnits = async (organizationId: any, selectedOrganizationUnitId: any, topOrganizationUnits: any) => {
    const topOrganizationUnitIds = new Set(topOrganizationUnits.map((orgUnit: any) => orgUnit.id));
    const organizationUnits = topOrganizationUnits
        .map((orgUnit: any) => ({
            ...orgUnit,
            isTop: true
        }));
    if (!(organizationUnits && organizationUnits.length)) {
        return [];
    }
    const selectedOrganizationUnit = organizationUnits
            .find((organizationUnit: any) => organizationUnit.id === selectedOrganizationUnitId)
        || organizationUnits[0];
    const displayedOrgUnits = organizationUnits.filter((organizationUnit: any) => organizationUnit.id !== selectedOrganizationUnit.id);
    const parentId = getParentOrganizationUnitId(selectedOrganizationUnit, topOrganizationUnitIds);
    displayedOrgUnits.push({
        ...selectedOrganizationUnit,
        isSelected: true,
        parentId
    });
    return displayedOrgUnits;
};
const getParentOrganizationUnitId = (organizationUnit: any, topOrganizationIds: any) => {
    return organizationUnit.parentId && (topOrganizationIds && !topOrganizationIds.has(organizationUnit.id))
        ? organizationUnit.parentId
        : undefined;
};
const getDisplayedOrganizationUnits = async (organizationId: any, organizationUnitId: any, topOrganizationUnits: any) => {
    const topOrganizationUnitIds = new Set(topOrganizationUnits.map((orgUnit: any) => orgUnit.id));
    const organizationUnit = await getOrganizationUnit(organizationId, organizationUnitId);
    const parentId = getParentOrganizationUnitId(organizationUnit, topOrganizationUnitIds);
    return [
        {
            ...organizationUnit,
            isSelected: true,
            parentId
        }
    ];
};
